/**
 * @fileoverview Types for Aramex WMS Integration
 *
 * Aramex is a Warehouse Management System (WMS) used for inventory visibility
 * across multiple regions (UAE, Saudi Arabia, Singapore).
 *
 * API Reference: https://cportal.infor.aramex.com/WS_EDI_TEST_V02/RestService_API/help
 */

import * as yup from 'yup'

// ============================================================================
// Common Types
// ============================================================================

/**
 * Amount with currency - used for monetary values
 */
export const aramexAmountSchema = yup.object({
  amount: yup.number().required(),
  currency_code: yup.string().required(),
})

export type AramexAmount = yup.InferType<typeof aramexAmountSchema>

// ============================================================================
// InquiryStock Request Types
// ============================================================================

/**
 * Application header for Aramex API requests
 */
export const aramexApplicationHeaderSchema = yup.object({
  /** Date of request in ISO format */
  RequestedDate: yup.string().required(),
  /** System identifier making the request */
  RequestedSystem: yup.string().required(),
  /** Unique transaction identifier for tracking */
  TransactionID: yup.string().required(),
})

export type AramexApplicationHeader = yup.InferType<typeof aramexApplicationHeaderSchema>

/**
 * Data header containing query parameters for InquiryStock
 */
export const aramexInquiryStockDataHeaderSchema = yup.object({
  /** Warehouse facility code (e.g., UAE, Saudi, Singapore) */
  Facility: yup.string().required(),
  /** Group results by SKU */
  GroupBySKU: yup.boolean().notRequired(),
  /** Include items with zero quantity in results */
  IncludeZeroQty: yup.boolean().notRequired(),
  /** Specific SKUs to query (array of SKU strings) */
  SKUList: yup.array().of(yup.string().required()).notRequired(),
  /** Partial SKU match filter */
  SKU_Contains: yup.string().notRequired(),
  /** Storer/customer identifier */
  StorerKey: yup.string().required(),
})

export type AramexInquiryStockDataHeader = yup.InferType<typeof aramexInquiryStockDataHeaderSchema>

/**
 * SSA Authentication credentials
 * Note: Actual credentials are stored in IntegrationCredential, not in request schema
 */
export const aramexSSAAuthSchema = yup.object({
  /** Login username */
  SSA_Login: yup.string().required(),
  /** Login password */
  SSA_Password: yup.string().required(),
  /** Alternative token-based authentication */
  SSA_Token: yup.string().notRequired(),
})

export type AramexSSAAuth = yup.InferType<typeof aramexSSAAuthSchema>

/**
 * User-defined data - array of custom key-value pairs
 */
export const aramexUserDataItemSchema = yup.object({
  Key: yup.string().required(),
  Value: yup.string().notRequired(),
})

export type AramexUserDataItem = yup.InferType<typeof aramexUserDataItemSchema>

/**
 * Complete InquiryStock request body
 */
export const aramexInquiryStockRequestSchema = yup.object({
  ApplicationHeader: aramexApplicationHeaderSchema.required(),
  DataHeader: aramexInquiryStockDataHeaderSchema.required(),
  SSA: aramexSSAAuthSchema.required(),
  UserDate: yup.array().of(aramexUserDataItemSchema).notRequired(),
})

export type AramexInquiryStockRequest = yup.InferType<typeof aramexInquiryStockRequestSchema>

// ============================================================================
// InquiryStock Response Types
// ============================================================================

/**
 * Individual inventory line item in the response
 */
export const aramexInventoryLineSchema = yup.object({
  // SKU Information
  SKU: yup.string().required(),
  Description: yup.string().nullable(),
  Manufacturer: yup.string().nullable(),
  HS_Code: yup.string().nullable(),
  BOE: yup.string().nullable(),

  // Quantity Fields
  QtyOnHand: yup.number().nullable(),
  QtyAvailable: yup.number().nullable(),
  QtyAllocated: yup.number().nullable(),
  QtyPicked: yup.number().nullable(),
  QtyHold: yup.number().nullable(),
  QtyDamage: yup.number().nullable(),
  QtyLost: yup.number().nullable(),

  // User-defined SKU fields (SUSR2-10)
  SKU_SUSR2: yup.string().nullable(),
  SKU_SUSR3: yup.string().nullable(),
  SKU_SUSR4: yup.string().nullable(),
  SKU_SUSR5: yup.string().nullable(),
  SKU_SUSR6: yup.string().nullable(),
  SKU_SUSR7: yup.string().nullable(),
  SKU_SUSR8: yup.string().nullable(),
  SKU_SUSR9: yup.string().nullable(),
  SKU_SUSR10: yup.string().nullable(),

  // Physical Properties
  UnitWGT: yup.number().nullable(),
  UnitCBM: yup.number().nullable(),

  // Lot tracking fields (Lottable01-10)
  Lottable01: yup.string().nullable(),
  Lottable02: yup.string().nullable(),
  Lottable03: yup.string().nullable(),
  Lottable04: yup.string().nullable(),
  Lottable05: yup.string().nullable(),
  Lottable06: yup.string().nullable(),
  Lottable07: yup.string().nullable(),
  Lottable08: yup.string().nullable(),
  Lottable09: yup.string().nullable(),
  Lottable10: yup.string().nullable(),

  // Timestamp
  RecordDate: yup.string().nullable(),
})

export type AramexInventoryLine = yup.InferType<typeof aramexInventoryLineSchema>

/**
 * Complete InquiryStock response
 */
export const aramexInquiryStockResponseSchema = yup.object({
  /** Array of inventory line items */
  Lines: yup.array().of(aramexInventoryLineSchema).nullable(),

  /** Response metadata */
  Response: yup
    .object({
      ErrorCode: yup.string().nullable(),
      ErrorDescription: yup.string().nullable(),
      ErrorType: yup.string().nullable(),
      Reference: yup.string().nullable(),
      Status: yup.string().nullable(),
      TransactionID: yup.string().nullable(),
    })
    .nullable(),
})

export type AramexInquiryStockResponse = yup.InferType<typeof aramexInquiryStockResponseSchema>

// ============================================================================
// Action Step Input Schema
// ============================================================================

/**
 * Action step input schema for Aramex InquiryStock
 * Used in workflow builder UI
 */
export const aramexInquiryStockActionStepInputSchema = yup.object({
  /** Group results by SKU (default: true) */
  groupBySKU: yup.boolean().notRequired().default(true),
  /** Include items with zero quantity (default: false) */
  includeZeroQty: yup.boolean().notRequired().default(false),
  /** Specific SKUs to query - comma-separated or array */
  skuList: yup.array().of(yup.string().required()).notRequired(),
  /** Partial SKU match filter */
  skuContains: yup.string().notRequired(),
})

export type AramexInquiryStockActionStepInput = yup.InferType<
  typeof aramexInquiryStockActionStepInputSchema
>

// ============================================================================
// Entity Types for Read Integration
// ============================================================================

/**
 * Supported entity types for Aramex read integration
 */
export enum AramexReadEntityType {
  INBOUND_ASN = 'INBOUND_ASN',
  STOCK_ADJUSTMENT = 'STOCK_ADJUSTMENT',
  STOCK = 'STOCK',
}

// ============================================================================
// InquiryASN Request Types
// ============================================================================

/**
 * Data header containing query parameters for InquiryASN
 */
export const aramexInquiryASNDataHeaderSchema = yup.object({
  /** Warehouse facility code (e.g., UAE, Saudi, Singapore) */
  Facility: yup.string().required(),
  /** Storer/customer identifier */
  StorerKey: yup.string().required(),
  /** Receipt key to query a specific ASN */
  ReceiptKey: yup.string().notRequired(),
  /** Client system reference (note: Aramex API uses this typo) */
  ClinetSystemRef: yup.string().notRequired(),
  /** Whether to include WMS line items in the response */
  IncludeLines: yup.boolean().notRequired(),
})

export type AramexInquiryASNDataHeader = yup.InferType<typeof aramexInquiryASNDataHeaderSchema>

/**
 * Complete InquiryASN request body
 */
export const aramexInquiryASNRequestSchema = yup.object({
  ApplicationHeader: aramexApplicationHeaderSchema.required(),
  DataHeader: aramexInquiryASNDataHeaderSchema.required(),
  SSA: aramexSSAAuthSchema.required(),
  UserDate: yup.array().of(aramexUserDataItemSchema).notRequired(),
})

export type AramexInquiryASNRequest = yup.InferType<typeof aramexInquiryASNRequestSchema>

// ============================================================================
// InquiryASN Response Types
// ============================================================================

/**
 * Serial number entry within a WMS line
 */
export const aramexASNSerialNumberSchema = yup.object({
  SerialNumber: yup.string().nullable(),
})

export type AramexASNSerialNumber = yup.InferType<typeof aramexASNSerialNumberSchema>

/**
 * Individual WMS line item within an ASN
 */
export const aramexASNLineSchema = yup.object({
  COO: yup.string().nullable(),
  ExpDate: yup.string().nullable(),
  ExternLineNo: yup.string().nullable(),
  Lottable01: yup.string().nullable(),
  Lottable02: yup.string().nullable(),
  Lottable07: yup.string().nullable(),
  Lottable08: yup.string().nullable(),
  Lottable09: yup.string().nullable(),
  Lottable10: yup.string().nullable(),
  ManuDate: yup.string().nullable(),
  NOTES: yup.string().nullable(),
  PURCHASEORDERDOCUMENT: yup.string().nullable(),
  PURCHASEORDERLINE: yup.string().nullable(),
  QtyExpected: yup.number().nullable(),
  QtyReceivecdALL: yup.number().nullable(),
  QtyReceivecdHOLD: yup.number().nullable(),
  QtyReceivecdOK: yup.number().nullable(),
  SKU: yup.string().nullable(),
  SUSR1: yup.string().nullable(),
  SUSR2: yup.string().nullable(),
  SUSR3: yup.string().nullable(),
  SUSR4: yup.string().nullable(),
  SUSR5: yup.string().nullable(),
  SerialNumbers: yup.array().of(aramexASNSerialNumberSchema).nullable(),
})

export type AramexASNLine = yup.InferType<typeof aramexASNLineSchema>

/**
 * Individual ASN record in the response
 */
export const aramexASNSchema = yup.object({
  AddDate: yup.string().nullable(),
  ArrivalDateTime: yup.string().nullable(),
  ClinetSystemRef: yup.string().nullable(),
  ClosedDate: yup.string().nullable(),
  EditDate: yup.string().nullable(),
  ExternalReceiptKey: yup.string().nullable(),
  IsUpdateAllowed: yup.boolean().nullable(),
  RMA: yup.string().nullable(),
  ReceiptKey: yup.string().nullable(),
  Status: yup.string().nullable(),
  Type: yup.string().nullable(),
  WMSLines: yup.array().of(aramexASNLineSchema).nullable(),
})

export type AramexASN = yup.InferType<typeof aramexASNSchema>

/**
 * Complete InquiryASN response
 */
export const aramexInquiryASNResponseSchema = yup.object({
  /** Array of ASN records */
  ASNs: yup.array().of(aramexASNSchema).nullable(),

  /** Response metadata */
  Response: yup
    .object({
      ErrorCode: yup.string().nullable(),
      ErrorDescription: yup.string().nullable(),
      ErrorType: yup.string().nullable(),
      Reference: yup.string().nullable(),
      Status: yup.string().nullable(),
      TransactionID: yup.string().nullable(),
    })
    .nullable(),
})

export type AramexInquiryASNResponse = yup.InferType<typeof aramexInquiryASNResponseSchema>

// ============================================================================
// InquiryASN Action Step Input Schema
// ============================================================================

/**
 * Action step input schema for Aramex InquiryASN
 * Used in workflow builder UI
 */
export const aramexInquiryASNActionStepInputSchema = yup.object({
  /** Receipt key to query a specific ASN */
  receiptKey: yup.string().notRequired(),
  /** Client system reference filter */
  clientSystemRef: yup.string().notRequired(),
  /** Whether to include WMS line items (default: true) */
  includeLines: yup.boolean().notRequired().default(true),
  /** Use InquiryASN_IncludeSerials endpoint to always return serial numbers (default: false) */
  includeSerials: yup.boolean().notRequired().default(false),
})

export type AramexInquiryASNActionStepInput = yup.InferType<
  typeof aramexInquiryASNActionStepInputSchema
>

// ============================================================================
// InquiryStockAdjustment Request Types
// ============================================================================

/**
 * Request body for InquiryStockAdjustment
 * Note: This endpoint uses a flat structure (no ApplicationHeader/DataHeader nesting)
 */
export const aramexInquiryStockAdjustmentRequestSchema = yup.object({
  /** Adjustment key to query a specific adjustment */
  AdjustmentKey: yup.string().notRequired(),
  /** Warehouse facility code */
  Facility: yup.string().required(),
  /** Receipt key filter */
  Receiptkey: yup.string().notRequired(),
  /** Specific SKUs to query */
  SKUList: yup.array().of(yup.string().required()).notRequired(),
  /** Partial SKU match filter */
  SKU_Contains: yup.string().notRequired(),
  /** SSA Authentication */
  SSA: aramexSSAAuthSchema.required(),
  /** Storer/customer identifier */
  StorerKey: yup.string().required(),
  /** User-defined data */
  UserDate: yup.array().of(aramexUserDataItemSchema).notRequired(),
})

export type AramexInquiryStockAdjustmentRequest = yup.InferType<
  typeof aramexInquiryStockAdjustmentRequestSchema
>

// ============================================================================
// InquiryStockAdjustment Response Types
// ============================================================================

/**
 * Individual stock adjustment line item in the response
 */
export const aramexStockAdjustmentLineSchema = yup.object({
  ADJUSTMENTKEY: yup.string().nullable(),
  ADJUSTMENTLINENUMBER: yup.string().nullable(),
  EFFECTIVEDATE: yup.string().nullable(),
  EXTERNADJUSTMENTKEY: yup.string().nullable(),
  ID: yup.string().nullable(),
  LOC: yup.string().nullable(),
  LOT: yup.string().nullable(),
  Qty: yup.number().nullable(),
  REASONCODE: yup.string().nullable(),
  RECEIPTKEY: yup.string().nullable(),
  RECEIPTLINENUMBER: yup.string().nullable(),
  REFERENCELINE: yup.string().nullable(),
  REFERENCELOCATION: yup.string().nullable(),
  REFERENCETYPE: yup.string().nullable(),
  SKU: yup.string().nullable(),
  STORERKEY: yup.string().nullable(),
  WHSEID: yup.string().nullable(),
})

export type AramexStockAdjustmentLine = yup.InferType<typeof aramexStockAdjustmentLineSchema>

/**
 * Complete InquiryStockAdjustment response
 */
export const aramexInquiryStockAdjustmentResponseSchema = yup.object({
  /** Array of stock adjustment line items */
  Lines: yup.array().of(aramexStockAdjustmentLineSchema).nullable(),

  /** Response metadata */
  Response: yup
    .object({
      ErrorCode: yup.string().nullable(),
      ErrorDescription: yup.string().nullable(),
      ErrorType: yup.string().nullable(),
      Reference: yup.string().nullable(),
      Status: yup.string().nullable(),
      TransactionID: yup.string().nullable(),
    })
    .nullable(),
})

export type AramexInquiryStockAdjustmentResponse = yup.InferType<
  typeof aramexInquiryStockAdjustmentResponseSchema
>

// ============================================================================
// InquiryStockAdjustment Action Step Input Schema
// ============================================================================

/**
 * Action step input schema for Aramex InquiryStockAdjustment
 * Used in workflow builder UI
 */
export const aramexInquiryStockAdjustmentActionStepInputSchema = yup.object({
  /** Adjustment key to query a specific adjustment */
  adjustmentKey: yup.string().notRequired(),
  /** Receipt key filter */
  receiptKey: yup.string().notRequired(),
  /** Specific SKUs to query */
  skuList: yup.array().of(yup.string().required()).notRequired(),
  /** Partial SKU match filter */
  skuContains: yup.string().notRequired(),
})

export type AramexInquiryStockAdjustmentActionStepInput = yup.InferType<
  typeof aramexInquiryStockAdjustmentActionStepInputSchema
>

// ============================================================================
// UpdatePOLines Request Types
// ============================================================================

/**
 * Address schema shared by BuyerAddress and SupplierAddress in PO requests
 */
export const aramexAddressSchema = yup.object({
  Address1: yup.string().notRequired(),
  Address2: yup.string().notRequired(),
  Address3: yup.string().notRequired(),
  Address4: yup.string().notRequired(),
  C_ID: yup.string().notRequired(),
  City: yup.string().notRequired(),
  Company: yup.string().notRequired(),
  Contact: yup.string().notRequired(),
  Contact2: yup.string().notRequired(),
  Country: yup.string().notRequired(),
  Email: yup.string().notRequired(),
  Fax: yup.string().notRequired(),
  Phone1: yup.string().notRequired(),
  Phone2: yup.string().notRequired(),
  Ref: yup.string().notRequired(),
  State: yup.string().notRequired(),
  W3W: yup.string().notRequired(),
  ZipCode: yup.string().notRequired(),
})

export type AramexAddress = yup.InferType<typeof aramexAddressSchema>

/**
 * Extra address schema for additional addresses in PO requests
 */
export const aramexExtraAddressSchema = yup.object({
  Address1: yup.string().notRequired(),
  Address2: yup.string().notRequired(),
  Address3: yup.string().notRequired(),
  Address4: yup.string().notRequired(),
  City: yup.string().notRequired(),
  Country: yup.string().notRequired(),
  Email: yup.string().notRequired(),
  Name1: yup.string().notRequired(),
  Name2: yup.string().notRequired(),
  Name3: yup.string().notRequired(),
  Name4: yup.string().notRequired(),
  Postal: yup.string().notRequired(),
  Ref: yup.string().notRequired(),
  SerialKey: yup.string().notRequired(),
  Telephone1: yup.string().notRequired(),
  Telephone2: yup.string().notRequired(),
  Type: yup.string().notRequired(),
  WMSRef: yup.string().notRequired(),
})

export type AramexExtraAddress = yup.InferType<typeof aramexExtraAddressSchema>

/**
 * PO data line schema for individual line items
 */
export const aramexPODataLineSchema = yup.object({
  AltSKU: yup.string().notRequired(),
  Consignment: yup.string().notRequired(),
  Descr: yup.string().notRequired(),
  ExternLineNo: yup.string().notRequired(),
  HSCODE: yup.string().notRequired(),
  InitalPromiseDate: yup.string().notRequired(),
  ManufacturerSKU: yup.string().notRequired(),
  NewPromiseDate: yup.string().notRequired(),
  Notes: yup.string().notRequired(),
  Qty: yup.number().notRequired(),
  RetailSKU: yup.string().notRequired(),
  SKU: yup.string().notRequired(),
  SUSR1: yup.string().notRequired(),
  SUSR2: yup.string().notRequired(),
  SUSR3: yup.string().notRequired(),
  SUSR4: yup.string().notRequired(),
  SUSR5: yup.string().notRequired(),
  UnitCost: yup.number().notRequired(),
  UserDate: yup.string().notRequired(),
})

export type AramexPODataLine = yup.InferType<typeof aramexPODataLineSchema>

/**
 * Data header for UpdatePOLines request
 */
export const aramexUpdatePOLinesDataHeaderSchema = yup.object({
  BuyerAddress: aramexAddressSchema.notRequired(),
  BuyerPO: yup.string().notRequired(),
  Carrier: yup.string().notRequired(),
  ClinetSystemRef: yup.string().notRequired(),
  ContainerQty: yup.number().notRequired(),
  ContainerType: yup.string().notRequired(),
  DOCreated: yup.string().notRequired(),
  ERPOrderKey: yup.string().notRequired(),
  Facility: yup.string().required(),
  IncoTerm: yup.string().notRequired(),
  Notes: yup.string().notRequired(),
  PODate: yup.string().notRequired(),
  POType: yup.string().notRequired(),
  StorerKey: yup.string().required(),
  SupplierAddress: aramexAddressSchema.notRequired(),
  SupplierCode: yup.string().notRequired(),
  SupplierName: yup.string().notRequired(),
  SUSR1: yup.string().notRequired(),
  SUSR2: yup.string().notRequired(),
  SUSR3: yup.string().notRequired(),
  SUSR4: yup.string().notRequired(),
  SUSR5: yup.string().notRequired(),
  WarehouseRef: yup.string().notRequired(),
})

export type AramexUpdatePOLinesDataHeader = yup.InferType<
  typeof aramexUpdatePOLinesDataHeaderSchema
>

/**
 * Complete UpdatePOLines request body
 */
export const aramexUpdatePOLinesRequestSchema = yup.object({
  ApplicationHeader: aramexApplicationHeaderSchema.required(),
  DataHeader: aramexUpdatePOLinesDataHeaderSchema.required(),
  DataLines: yup.array().of(aramexPODataLineSchema).notRequired(),
  ExtraAddresses: yup.array().of(aramexExtraAddressSchema).notRequired(),
  SSA: aramexSSAAuthSchema.required(),
  UserDate: yup.array().of(aramexUserDataItemSchema).notRequired(),
})

export type AramexUpdatePOLinesRequest = yup.InferType<typeof aramexUpdatePOLinesRequestSchema>

// ============================================================================
// UpdatePOLines Response Types
// ============================================================================

/**
 * UpdatePOLines response - flat structure (like InquiryStock)
 */
export const aramexUpdatePOLinesResponseSchema = yup.object({
  ErrorCode: yup.string().nullable(),
  ErrorDescription: yup.string().nullable(),
  ErrorType: yup.string().nullable(),
  Reference: yup.string().nullable(),
  Status: yup.string().nullable(),
  TransactionID: yup.string().nullable(),
})

export type AramexUpdatePOLinesResponse = yup.InferType<typeof aramexUpdatePOLinesResponseSchema>

// ============================================================================
// UpdatePOLines Action Step Input Schema
// ============================================================================

/**
 * Action step input schema for Aramex UpdatePOLines
 * Used in workflow builder UI - camelCase input that maps to API request fields
 */
export const aramexUpdatePOLinesActionStepInputSchema = yup.object({
  /** Buyer PO reference */
  buyerPO: yup.string().notRequired(),
  /** Carrier code */
  carrier: yup.string().notRequired(),
  /** Client system reference */
  clientSystemRef: yup.string().notRequired(),
  /** Container quantity */
  containerQty: yup.number().notRequired(),
  /** Container type */
  containerType: yup.string().notRequired(),
  /** Date created */
  doCreated: yup.string().notRequired(),
  /** ERP order key */
  erpOrderKey: yup.string().notRequired(),
  /** Incoterm */
  incoTerm: yup.string().notRequired(),
  /** Notes */
  notes: yup.string().notRequired(),
  /** PO date */
  poDate: yup.string().notRequired(),
  /** PO type */
  poType: yup.string().notRequired(),
  /** Supplier code */
  supplierCode: yup.string().notRequired(),
  /** Supplier name */
  supplierName: yup.string().notRequired(),
  /** User-defined fields */
  susr1: yup.string().notRequired(),
  susr2: yup.string().notRequired(),
  susr3: yup.string().notRequired(),
  susr4: yup.string().notRequired(),
  susr5: yup.string().notRequired(),
  /** Warehouse reference */
  warehouseRef: yup.string().notRequired(),
  /** Buyer address */
  buyerAddress: aramexAddressSchema.notRequired(),
  /** Supplier address */
  supplierAddress: aramexAddressSchema.notRequired(),
  /** PO data lines */
  dataLines: yup.array().of(aramexPODataLineSchema).notRequired(),
  /** Extra addresses */
  extraAddresses: yup.array().of(aramexExtraAddressSchema).notRequired(),
})

export type AramexUpdatePOLinesActionStepInput = yup.InferType<
  typeof aramexUpdatePOLinesActionStepInputSchema
>
